export const DEFAULT_PLAN_OPTIONS = [
  {
    type: 'monthly',
    label: 'Basic Monthly',
    priceCents: 500,
    coveredMonths: 1,
    discountSupported: false,
  },
  {
    type: 'year',
    label: 'Basic Annual',
    priceCents: 5000,
    coveredMonths: 12,
    discountSupported: true,
  },
  {
    type: 'cc-monthly',
    label: 'Standard Monthly',
    priceCents: 1000,
    coveredMonths: 1,
    discountSupported: false,
  },
  {
    type: 'cc-year',
    label: 'Standard Annual',
    priceCents: 10000,
    coveredMonths: 12,
    discountSupported: true,
  },
];

export const AFFILIATE_BEHAVIOR_TYPES = {
  SPLIT_EARNINGS: 'split_earnings',
  ALL_EARNINGS: 'all_earnings',
  ALL_DISCOUNT: 'all_discount',
};

export const AFFILIATE_BEHAVIOR_OPTIONS = [
  {
    value: AFFILIATE_BEHAVIOR_TYPES.SPLIT_EARNINGS,
    label: 'Split Earnings (default)',
    description: 'You earn 10% of the plan list price; they save 10% on annual prepaid.',
  },
  {
    value: AFFILIATE_BEHAVIOR_TYPES.ALL_EARNINGS,
    label: 'All Earnings',
    description: 'You earn 20% of the plan list price; they do not receive a plan discount.',
  },
  {
    value: AFFILIATE_BEHAVIOR_TYPES.ALL_DISCOUNT,
    label: 'All Discount',
    description: 'You earn no commission; they save 20% on an annual prepaid plan.',
  },
];

function optionalInteger(value) {
  if (value === '' || value === null || value === undefined) return undefined;
  return Number.parseInt(value, 10);
}

function inferBehaviorType(code) {
  if (Object.prototype.hasOwnProperty.call(code || {}, 'behaviorType')) {
    return AFFILIATE_BEHAVIOR_OPTIONS.some(option => option.value === code.behaviorType)
      ? code.behaviorType
      : '';
  }
  const behavior = code?.behavior || {};
  const annualDiscount = behavior.byCadence?.annual?.discountPercentBps || 0;
  if (behavior.commissionRateBps === 2000 && annualDiscount === 0) {
    return AFFILIATE_BEHAVIOR_TYPES.ALL_EARNINGS;
  }
  if ((behavior.commissionRateBps || 0) === 0 && annualDiscount === 2000) {
    return AFFILIATE_BEHAVIOR_TYPES.ALL_DISCOUNT;
  }
  return AFFILIATE_BEHAVIOR_TYPES.SPLIT_EARNINGS;
}

export function affiliateCodeToFormValues(code = {}) {
  const affiliateCode = code || {};
  return {
    code: affiliateCode.displayCode || affiliateCode.code || '',
    name: affiliateCode.name || '',
    description: affiliateCode.description || '',
    behavior: inferBehaviorType(affiliateCode),
    maxRedemptions: affiliateCode.maxRedemptions || '',
    expiresAt: affiliateCode.expiresAt
      ? String(affiliateCode.expiresAt).slice(0, 10)
      : '',
  };
}

export function validateAffiliateCodeForm(values) {
  const errors = {};
  const normalizedCode = String(values.code || '').trim().toUpperCase()
    .replace(/[\s_.]+/g, '');
  if (!/^[A-Z0-9-]{3,64}$/.test(normalizedCode)) {
    errors.code = 'Use 3-64 letters, numbers, or hyphens.';
  }
  if (!AFFILIATE_BEHAVIOR_OPTIONS.some(option => option.value === values.behavior)) {
    errors.behavior = 'Choose an affiliate code type.';
  }
  const maxRedemptions = optionalInteger(values.maxRedemptions);
  if (maxRedemptions !== undefined && maxRedemptions < 1) {
    errors.maxRedemptions = 'Maximum referrals must be at least 1.';
  }
  return errors;
}

export function serializeAffiliateCodeForm(values) {
  return {
    code: String(values.code || '').trim(),
    displayCode: String(values.code || '').trim(),
    name: String(values.name || '').trim() || null,
    description: String(values.description || '').trim() || null,
    behavior: values.behavior,
    maxRedemptions: optionalInteger(values.maxRedemptions) || null,
    expiresAt: values.expiresAt || null,
  };
}

function behaviorTerms(type) {
  if (type === AFFILIATE_BEHAVIOR_TYPES.ALL_EARNINGS) {
    return { commissionPercent: 20, annualDiscountPercent: 0 };
  }
  if (type === AFFILIATE_BEHAVIOR_TYPES.ALL_DISCOUNT) {
    return { commissionPercent: 0, annualDiscountPercent: 20 };
  }
  return { commissionPercent: 10, annualDiscountPercent: 10 };
}

export function buildAffiliatePreview(values, planOptions = DEFAULT_PLAN_OPTIONS) {
  const terms = behaviorTerms(values.behavior);
  return planOptions.map(plan => {
    const discountPercent = plan.discountSupported ? terms.annualDiscountPercent : 0;
    const discountCents = Math.round(plan.priceCents * discountPercent / 100);
    const commissionPerPayment = Math.round(
      plan.priceCents * terms.commissionPercent / 100,
    );
    const paymentCount = terms.commissionPercent > 0
      ? Math.max(1, Math.ceil(12 / plan.coveredMonths))
      : 0;
    return {
      ...plan,
      discountCents,
      commissionCents: commissionPerPayment * paymentCount,
      paymentCount,
    };
  });
}

export function formatCents(amount) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format((amount || 0) / 100);
}
